/**
 * El módulo de mantenimiento web continuo, del lado del navegador.
 *
 * Todo lo que hay acá ya existía y solo se llegaba por el chat de Evo Brain.
 * Esta es la otra vía, no el reemplazo (Eduardo, 2026-09-05).
 *
 * Una decisión que atraviesa el archivo: los tipos copian la forma del server
 * TAL CUAL, incluido `Eje<T>` — medido, o el motivo por el que no. Aplanarlo a
 * un número con cero de default sería el error que el server evita a propósito:
 * le diría al cliente que no tuvo visitas cuando la verdad es que nunca miramos.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Tipos, espejo de packages/server/src/services/maintenance/ ───────────────

export type Autonomia = 'manual' | 'semi' | 'auto';
export type Cadencia = 'apagado' | 'semanal' | 'quincenal' | 'mensual';

// ── La revisión semanal (services/maintenance/revision.ts) ───────────────
export interface ResumenDeRevision {
  autonomia: Autonomia;
  puntajes: { accesibilidad: number | null; seo: number | null; velocidad: number | null };
  hallazgos: { total: number; automatico: number; texto: number; bloqueado: number } | null;
  arreglosAutomaticos: string[];
  borradores: { propuesta: number; bien: number; decidir: number; error: number; aplicados: number };
  temas: { nuevos: number; total: number; motivo: string | null };
  palabras: string[];
  articulos: { escritos: number[]; fechas: string[]; esperandoDeAntes: number; motivo: string | null };
  reporte: { reportId: number | null; enviado: boolean; motivo: string | null };
  errores: string[];
}
export interface RevisionGuardada {
  id: number;
  projectId: number;
  empezoEn: string;
  terminoEn: string | null;
  estado: 'corriendo' | 'lista' | 'fallo';
  resumen: ResumenDeRevision | null;
  avisadaEn: string | null;
}
export interface EstadoRevision {
  ultima: RevisionGuardada | null;
  /** La fecha (YYYY-MM-DD, zona del negocio) de la próxima, o null con el módulo apagado. */
  proxima: { fecha: string; hora: number; dia: number } | null;
  /** 0 = domingo … 6 = sábado. */
  dia: number;
  hora: number;
}
export type FuenteDelTema = 'busqueda' | 'web' | 'gsc' | 'sitio' | 'nicho';
/** Una consulta que la gente escribe en Google (autocompletar), sin cifras. */
export interface Palabra {
  consulta: string;
  semilla: string;
  orden: number;
  veces: number;
  yaAparece: { impresiones: number; posicion: number } | null;
}
export type FuenteDelNicho = 'manual' | 'deducido';
export type EstadoArticulo = 'borrador' | 'cambios' | 'aprobado' | 'publicado';

export interface Medicion {
  id: number;
  auditedAt: string;
  origin: string;
  targetVersion: number;
  score: number | null;
  accesibilidad: { critical: number; serious: number; moderate: number; minor: number };
  seo: unknown[] | null;
}

export interface Comparacion {
  desde: Medicion;
  hasta: Medicion;
  deltaScore: number | null;
  deltaSeo: number | null;
  deltaCriticos: number | null;
  publicaciones: number;
}

export interface EstadoMantenimiento {
  enabled: boolean;
  autonomia: Autonomia;
  autonomiaDescripcion: string;
  serie: Medicion[];
  comparacion: Comparacion | null;
  /** Cuántos artículos escribe la revisión semanal. 0 = ninguno. */
  articulosPorSemana: number;
  opcionesPorSemana: number[];
  revision: EstadoRevision;
  /** El estilo de las portadas, una frase. Null = el sobrio por defecto. */
  estiloImagenes: string | null;
}

export interface ConexionSearchConsole {
  projectId: number;
  property: string;
  status: string;
  checkedAt: string | null;
  lastError: string | null;
}

export interface EstadoSearchConsole {
  conexion: ConexionSearchConsole | null;
  /** La propiedad que casi seguro es, a partir de la dirección del sitio. */
  sugerida: string | null;
  /** Por qué lo guardado no es una propiedad (un correo pegado, p. ej.), o null. */
  propiedadInvalida: string | null;
  queFalta: string | null;
  cuentaDeServicio: string | null;
}

export interface Nicho {
  texto: string;
  fuente: FuenteDelNicho;
  actualizadoEn: string | null;
}

export interface Tema {
  titulo: string;
  porQue: string;
  fuente: FuenteDelTema;
  evidencia: string;
}

/** Un tema guardado (services/maintenance/temasGuardados.ts). Persiste entre sesiones. */
export type EstadoDelTema = 'propuesto' | 'marcado' | 'descartado' | 'usado';
export interface TemaGuardado extends Tema {
  id: number;
  projectId: number;
  reviewId: number | null;
  estado: EstadoDelTema;
  marcadoPor: 'evo' | 'eduardo' | null;
  articuloId: number | null;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Evidencia {
  nicho: string | null;
  consultas: Array<{ consulta: string; impresiones: number; clicks: number; posicion: number }>;
  /** Consultas que quedaron fuera por ser de marca. */
  consultasDeMarca: number;
  seccionesDelSitio: string[];
  sinConsultasPorque: string | null;
  /** Lo que la búsqueda web trajo, o por qué no. */
  tendencias: Tema[];
  sinTendenciasPorque: string | null;
  /** Lo que la gente escribe en Google sobre el nicho, o por qué no hay. */
  palabras: Palabra[];
  sinPalabrasPorque: string | null;
}

export interface Articulo {
  id: number;
  projectId: number;
  topicTitle: string;
  topicSource: FuenteDelTema;
  topicEvidence: string;
  title: string;
  slug: string;
  excerpt: string;
  bodyHtml: string;
  status: EstadoArticulo;
  publicToken: string | null;
  sentToClientAt: string | null;
  clientDecision: 'aprobado' | 'comentado' | null;
  clientComment: string | null;
  clientDecidedAt: string | null;
  changesRequestedBy: 'eduardo' | 'cliente' | null;
  changesNote: string | null;
  scheduledFor: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  publishedPath: string | null;
  /** La portada, si se generó. Se ve por /articulos/:id/portada. */
  coverPath: string | null;
  /** La revisión semanal que lo escribió, si fue una. */
  reviewId: number | null;
  createdAt: string;
  updatedAt: string;
  urlDelCliente: string | null;
  /** 'es' | 'en': el idioma de esta pieza. */
  lang: string;
  /** La misma pieza en las otras versiones del sitio (/en/), si el sitio las tiene. */
  traducciones: Array<{ id: number; lang: string; title: string; status: string }>;
}

/** Medido, o el motivo por el que no. Un motivo NO es un cero. */
export type Eje<T> =
  | { hay: true; dato: T }
  | { hay: false; codigo: string; porque: string };

export interface CambioDePuntaje {
  antes: number;
  despues: number;
  delta: number;
  publicaciones: number;
  hallazgosSeoAntes: number | null;
  hallazgosSeoDespues: number | null;
  criticosAntes: number;
  criticosDespues: number;
}

export interface CambioDeBusquedas {
  periodo: { clicks: number; impresiones: number; posicion: number };
  anterior: { clicks: number; impresiones: number; posicion: number } | null;
  consultas: Array<{ consulta: string; clicks: number; impresiones: number; posicion: number }>;
}

export interface DatosDelPeriodo {
  projectId: number;
  negocio: string;
  ventana: { desde: string; hasta: string; recienEmpieza?: boolean };
  puntaje: Eje<CambioDePuntaje>;
  puntoDePartida?: { puntaje: number; fecha: string } | null;
  busquedas: Eje<CambioDeBusquedas>;
  articulos: Array<{ titulo: string; publicadoEn: string; ruta: string | null; deQueSalio: string }>;
  vacio: boolean;
}

export interface Recomendacion {
  cadencia: Cadencia;
  publicaciones: number;
  articulos: number;
  movimientos: number;
  porque: string;
}

export interface EstadoDelPeriodo {
  datos: DatosDelPeriodo;
  cadenciaActual: Cadencia;
  cadenciaRecomendada: Recomendacion | null;
  ultimoReporteEnviado: string | null;
  /** Preparado y todavía no enviado. Sobrevive a recargar la pantalla. */
  reportePreparado: { reportId: number; slug: string; url: string; creadoEn: string } | null;
}

// ── 1 · Estado y autonomía ───────────────────────────────────────────────────

const base = (projectId: number) => `/api/projects/${String(projectId)}`;

export function useMantenimiento(projectId: number) {
  return useQuery<EstadoMantenimiento>({
    queryKey: ['mantenimiento', projectId],
    queryFn: () => api.get(`${base(projectId)}/maintenance`),
  });
}

/**
 * Encender congela la línea de base. Apagar no borra la historia.
 *
 * Invalida la serie además del estado: encender crea la medición cero, y sin
 * refrescar la pantalla mostraría "encendido, sin historia" hasta recargar.
 */
export function useEncenderMantenimiento(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<{ enabled: boolean; baseline: Medicion | null }>(`${base(projectId)}/maintenance`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] });
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
    },
  });
}

/** Cada cuánto escribe Evo Brain un artículo solo. */
/** El estilo de las portadas: una frase por proyecto. Vacía vuelve al sobrio por defecto. */
export function useFijarEstiloImagenes(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (estilo: string) =>
      api.put<{ estiloImagenes: string | null }>(`${base(projectId)}/articulos/estilo-imagenes`, { estilo }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] }); },
  });
}

/** Cuántos artículos por semana escribe la revisión. 0 apaga la escritura. */
export function useFijarArticulosPorSemana(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (porSemana: number) =>
      api.put<{ porSemana: number }>(`${base(projectId)}/articulos/ritmo`, { porSemana }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] }); },
  });
}

// ── 2 · La revisión semanal ──────────────────────────────────────────────────

/**
 * La última revisión y la próxima. Mientras una está corriendo se relee cada
 * pocos segundos: tarda minutos y el panel tiene que verla terminar sin que
 * nadie recargue.
 */
export function useRevision(projectId: number, enabled: boolean) {
  return useQuery<EstadoRevision>({
    queryKey: ['mantenimiento', projectId, 'revision'],
    queryFn: () => api.get(`${base(projectId)}/maintenance/revision`),
    enabled,
    refetchInterval: q => (q.state.data?.ultima?.estado === 'corriendo' ? 5_000 : false),
  });
}

export function useCorrerRevision(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<EstadoRevision>(`${base(projectId)}/maintenance/revision/correr`, {}),
    onSuccess: data => {
      qc.setQueryData(['mantenimiento', projectId, 'revision'], data);
    },
  });
}

export function useAjustarRevision(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ajustes: { dia?: number; hora?: number }) =>
      api.put<EstadoRevision>(`${base(projectId)}/maintenance/revision`, ajustes),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] }); },
  });
}

export function useFijarAutonomia(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nivel: Autonomia) =>
      api.put<{ autonomia: Autonomia; descripcion: string }>(`${base(projectId)}/maintenance/autonomia`, { nivel }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] }); },
  });
}

// ── 3 · Search Console ───────────────────────────────────────────────────────

export function useSearchConsole(projectId: number, enabled: boolean) {
  return useQuery<EstadoSearchConsole>({
    queryKey: ['search-console', projectId],
    queryFn: () => api.get(`${base(projectId)}/search-console`),
    enabled,
  });
}

/** Con `property` conecta; sin ella, recomprueba la que ya está. */
export function useConectarSearchConsole(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (property?: string) =>
      api.post<EstadoSearchConsole>(`${base(projectId)}/search-console`, property ? { property } : {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['search-console', projectId] }); },
  });
}

export function useDesconectarSearchConsole(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ conexion: null }>(`${base(projectId)}/search-console`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['search-console', projectId] }); },
  });
}

// ── 4 · Los artículos ────────────────────────────────────────────────────────

export function useArticulos(projectId: number, enabled: boolean) {
  return useQuery<{ articulos: Articulo[] }>({
    queryKey: ['articulos', projectId],
    queryFn: () => api.get(`${base(projectId)}/articulos`),
    enabled,
  });
}

/**
 * Una sola mutación para las acciones sobre un artículo.
 *
 * Cinco hooks casi idénticos serían cinco lugares donde olvidarse de invalidar
 * la lista, y el síntoma —una pantalla que muestra el estado viejo— parece un
 * problema del servidor.
 */
type AccionArticulo =
  | { tipo: 'aprobar'; fecha?: string }
  // Agendar SIN aprobar: la fecha queda puesta y el artículo espera al cliente.
  | { tipo: 'fecha'; fecha: string }
  | { tipo: 'cambios'; nota: string }
  | { tipo: 'reescribir' }
  | { tipo: 'publicar' }
  | { tipo: 'compartir' }
  | { tipo: 'revocar' }
  | { tipo: 'portada' }
  | { tipo: 'borrar' };

type RespuestaAccion = { articulo: Articulo } | { ok: true };

/** Lo que devuelve el envío del correo de revisión. Ver avisoDeRevision.ts. */
export interface EnvioDeRevision {
  ok: boolean;
  a?: string;
  motivo?: string;
  detalle?: string;
}

export function useAccionArticulo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ articuloId, accion }: { articuloId: number; accion: AccionArticulo }): Promise<RespuestaAccion> => {
      const u = `${base(projectId)}/articulos/${String(articuloId)}`;
      switch (accion.tipo) {
        case 'aprobar':
          return api.post<{ articulo: Articulo }>(`${u}/aprobar`, accion.fecha ? { fecha: accion.fecha } : {});
        case 'fecha':
          return api.put<{ articulo: Articulo }>(`${u}/fecha`, { fecha: accion.fecha });
        case 'cambios':
          return api.post<{ articulo: Articulo }>(`${u}/cambios`, { nota: accion.nota });
        case 'reescribir':
          return api.post<{ articulo: Articulo }>(`${u}/reescribir`, {});
        case 'publicar':
          return api.post<{ articulo: Articulo }>(`${u}/publicar`, {});
        case 'compartir':
          // Trae el resultado del envío: desde el 2026-09-17 este botón manda un
          // correo de verdad al cliente, y un botón que manda correo sin decir
          // si salió es un botón que miente.
          return api.post<{ articulo: Articulo; envio?: EnvioDeRevision }>(`${u}/compartir`, {});
        case 'revocar':
          return api.delete<{ articulo: Articulo }>(`${u}/compartir`);
        case 'portada':
          return api.post<{ articulo: Articulo }>(`${u}/portada`, {});
        case 'borrar':
          return api.delete<{ ok: true }>(u);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['articulos', projectId] });
      // Publicar cambia la serie: cada publicación deja una medición. Borrar
      // devuelve el tema a la lista.
      void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] });
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
      void qc.invalidateQueries({ queryKey: ['temas', projectId] });
    },
  });
}

// ── 5 · Los temas y el nicho ─────────────────────────────────────────────────

export function useNicho(projectId: number, enabled: boolean) {
  return useQuery<{ nicho: Nicho | null }>({
    queryKey: ['nicho', projectId],
    queryFn: () => api.get(`${base(projectId)}/nicho`),
    enabled,
  });
}

/** Con texto lo fija a mano; sin texto, Evo Brain lo deduce del material. */
export function useFijarNicho(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (texto?: string) =>
      api.post<{ nicho: Nicho }>(`${base(projectId)}/nicho`, texto ? { texto } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['nicho', projectId] });
      void qc.invalidateQueries({ queryKey: ['temas', projectId] });
    },
  });
}

/**
 * Los temas GUARDADOS. Leerlos no cuesta nada: es la lista que la revisión
 * semanal (o «Buscar temas ahora») dejó. Persisten entre sesiones y se
 * marcan: Eduardo (2026-09-06), "los tópicos deberían persistir".
 */
export function useTemas(projectId: number, enabled: boolean, todos = false) {
  return useQuery<{ temas: TemaGuardado[] }>({
    queryKey: ['temas', projectId, todos],
    queryFn: () => api.get(`${base(projectId)}/temas${todos ? '?todos=1' : ''}`),
    enabled,
  });
}

/** Busca temas nuevos con el modelo y los guarda. Tarda; se pide cuando se pide. */
export function useBuscarTemas(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ temas: TemaGuardado[]; evidencia: Evidencia }>(`${base(projectId)}/temas/buscar`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['temas', projectId] }); },
  });
}

export function useMarcarTema(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ temaId, estado }: { temaId: number; estado: 'marcado' | 'descartado' | 'propuesto' }) =>
      api.put<{ tema: TemaGuardado }>(`${base(projectId)}/temas/${String(temaId)}`, { estado }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['temas', projectId] }); },
  });
}

export function useEscribirArticulo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tema: TemaGuardado) =>
      api.post<{ articulo: Articulo }>(`${base(projectId)}/articulos`, {
        tema: { titulo: tema.titulo, porQue: tema.porQue, fuente: tema.fuente, evidencia: tema.evidencia },
        temaId: tema.id,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['articulos', projectId] });
      void qc.invalidateQueries({ queryKey: ['temas', projectId] });
    },
  });
}

// ── 6 · Los reportes ─────────────────────────────────────────────────────────

export function usePeriodo(projectId: number, enabled: boolean) {
  return useQuery<EstadoDelPeriodo>({
    queryKey: ['periodo', projectId],
    queryFn: () => api.get(`${base(projectId)}/periodo`),
    enabled,
  });
}

export function useFijarCadencia(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cadencia: Cadencia) => api.put<{ cadencia: Cadencia }>(`${base(projectId)}/cadencia`, { cadencia }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}

export interface ReportePreparado {
  reportId: number;
  slug: string;
  /** El enlace público, el mismo formato que el resto de los reportes. */
  url: string;
  datos: DatosDelPeriodo;
}

/** Arma el reporte para revisar. NO lo manda. */
export function usePrepararReporte(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ reporte: ReportePreparado }>(`${base(projectId)}/reporte`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

/** Sale de verdad, al correo del cliente. No es un ensayo. */
export function useEnviarReporte(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: number) =>
      api.post<{ enviado: { a?: string } }>(`${base(projectId)}/reporte/${String(reportId)}/enviar`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}
